import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import AgentSelectorModal from './AgentSelectorModal';
import ChunkedFileUploader from './ChunkedFileUploader';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function Agent00Management({ companyId, userToken }) {
  const [selectedModule, setSelectedModule] = useState('');
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  
  // Get token from localStorage if not provided via props
  const token = userToken || localStorage.getItem('token');
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get company modules - use same query as CompanyDashboard
  const { data: allModules = [], isLoading: modulesLoading, error: modulesError, isSuccess } = useQuery({
    queryKey: ['companyModules', companyId],
    queryFn: async () => {
      if (!companyId || !token) {
        throw new Error('Missing companyId or token');
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/companies/${companyId}/modules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    enabled: !!companyId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 2
  });

  // Filter enabled modules
  const enabledModules = React.useMemo(() => {
    if (!allModules || allModules.length === 0) return [];
    return allModules.filter(m => m.is_enabled === true);
  }, [allModules]);

  const handleUploadComplete = (data) => {
    setCurrentJobId(data.job_id);
    
    toast({
      title: "Archivos procesados",
      description: "Selecciona los agentes que deseas ejecutar",
    });

    // Show agent selector modal
    setShowAgentSelector(true);
  };

  const handleUploadError = (errorMessage) => {
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
  };

  const handleAgentExecutionSuccess = () => {
    // Refetch insights and related queries
    queryClient.invalidateQueries(['insights', companyId]);
    queryClient.invalidateQueries(['insights-reports', companyId]);
    queryClient.invalidateQueries(['threads', companyId]);
    queryClient.invalidateQueries(['kpis', companyId]);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Ingesta de Datos</h1>
        <p className="text-muted-foreground mt-2">
          Sube archivos de datos (CSV, Excel, PDF) y selecciona los agentes que deseas ejecutar. 
          Soporta archivos de cualquier tamaño, incluyendo archivos grandes de más de 500MB.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Seleccionar Módulo</CardTitle>
          <CardDescription>Elige el módulo al que pertenecen los datos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!companyId || !token ? (
            <div className="p-3 bg-yellow-900/20 border border-yellow-900/30 rounded text-yellow-400 text-sm">
              Esperando datos de sesión... Por favor, espera un momento.
            </div>
          ) : modulesError ? (
            <div className="p-3 bg-red-900/20 border border-red-900/30 rounded text-red-400 text-sm">
              Error al cargar módulos: {modulesError.message}
            </div>
          ) : null}
          
          <Select 
            value={selectedModule} 
            onValueChange={setSelectedModule}
            disabled={modulesLoading || !companyId || !token}
          >
            <SelectTrigger>
              <SelectValue 
                placeholder={
                  modulesLoading ? "Cargando módulos..." : 
                  !companyId || !token ? "Esperando sesión..." :
                  "Selecciona un módulo"
                } 
              />
            </SelectTrigger>
            <SelectContent data-radix-popper-content-wrapper="" style={{ zIndex: 9999 }}>
              {enabledModules.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground">
                  {modulesLoading ? 'Cargando...' : 'No hay módulos habilitados'}
                </div>
              ) : (
                enabledModules.map((module) => (
                  <SelectItem 
                    key={module.id || module.module_type} 
                    value={module.module_type}
                  >
                    {module.custom_name || module.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          
          {isSuccess && allModules.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {enabledModules.length} módulo(s) habilitado(s) de {allModules.length} total
            </p>
          )}
        </CardContent>
      </Card>

      {selectedModule && (
        <Card>
          <CardHeader>
            <CardTitle>Subir Archivos de Datos</CardTitle>
            <CardDescription>
              Sube uno o más archivos (CSV, Excel, PDF). Soporta archivos de cualquier tamaño.
              Archivos mayores a 500MB se subirán automáticamente en fragmentos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChunkedFileUploader
              companyId={companyId}
              moduleType={selectedModule}
              token={token}
              onUploadComplete={handleUploadComplete}
              onUploadError={handleUploadError}
              disabled={!companyId || !token}
            />
          </CardContent>
        </Card>
      )}

      {/* Agent Selector Modal */}
      <AgentSelectorModal
        isOpen={showAgentSelector}
        onClose={() => setShowAgentSelector(false)}
        jobId={currentJobId}
        companyId={companyId}
        moduleType={selectedModule}
        userToken={token}
        onSuccess={handleAgentExecutionSuccess}
      />
    </div>
  );
}

export default Agent00Management;
